import { FullScreenPass } from "@faro-lotv/lotv";
import {
  Camera,
  DataTexture,
  FloatType,
  Material,
  MathUtils,
  Matrix4,
  NearestFilter,
  NoBlending,
  RGBAFormat,
  RGFormat,
  ShaderMaterial,
  Vector3,
  Texture,
  Uniform,
  WebGLRenderer,
  WebGLRenderTarget,
} from "three";
import type { SurfaceSelection } from "./surface-selection-pass";

const METADATA_COLUMNS = 11;
const MATRIX_COLUMNS = 4;
const MAX_CELL_HASH_SIZE = 4_194_304;
const CELL_HASH_X = 73856093;
const CELL_HASH_Y = 19349663;
const CELL_HASH_Z = 83492791;
const FILTER_IDENTIFIERS = new RegExp(
  "\\b(u[A-Z]\\w*|SURFACE_FILTER_SKIP_[A-Z]+|surfaceMetadata|surfaceMatrix|selectedSurfacePoint" +
    "|analyticSurfacePoint|pipeSurfacePoint|processedCell|cellEntry)\\b",
  "g",
);
const shader = `
uniform float uOtherPointsOpacity;
uniform bool uSurfaceFilterEnabled;
uniform bool uUseCells;
#ifndef SURFACE_FILTER_SKIP_ANALYTIC
uniform sampler2D uSurfaceAtlas;
uniform sampler2D uSurfaceMetadata;
uniform vec2 uSurfaceAtlasSize;
uniform int uSurfaceCount;
#endif
#ifndef SURFACE_FILTER_SKIP_CELLS
uniform mat4 uCellFrame;
uniform sampler2D uCellHash;
uniform float uCellHashSize;
uniform float uCellHashWidth;
vec4 cellEntry(float index) {
  return texture2D(uCellHash, (vec2(mod(index, uCellHashWidth), floor(index / uCellHashWidth)) + 0.5) / uCellHashWidth);
}
bool processedCell(vec4 world) {
  vec3 point = (uCellFrame * world).xyz;
  vec3 cell = floor(point / 0.25);
  uvec3 indices = uvec3(ivec3(cell));
  uint hash = (indices.x * 73856093u) ^ (indices.y * 19349663u) ^ (indices.z * 83492791u);
  float slot = float(hash % uint(uCellHashSize));
  for (int probe = 0; probe < 64; probe++) {
    vec4 entry = cellEntry(slot);
    if (entry.w < 0.5) return false;
    if (all(equal(entry.xyz, cell))) return true;
    slot = mod(slot + 1.0, uCellHashSize);
  }
  return false;
}
#endif
#ifndef SURFACE_FILTER_SKIP_ANALYTIC
vec4 surfaceMetadata(float column, int index) {
  return texture2D(uSurfaceMetadata, vec2((column + 0.5) / 11.0, (float(index) + 0.5) / float(uSurfaceCount)));
}
mat4 surfaceMatrix(float column, int index) {
  return mat4(surfaceMetadata(column, index), surfaceMetadata(column + 1.0, index),
    surfaceMetadata(column + 2.0, index), surfaceMetadata(column + 3.0, index));
}
#endif
bool selectedSurfacePoint(vec4 world) {
#ifndef SURFACE_FILTER_SKIP_CELLS
  if (uUseCells) return processedCell(world);
#endif
#ifndef SURFACE_FILTER_SKIP_ANALYTIC
  for (int index = 0; index < uSurfaceCount; index++) {
    vec4 projected = surfaceMatrix(0.0, index) * world;
    vec3 ndc = projected.xyz / projected.w;
    vec4 bounds = surfaceMetadata(9.0, index);
    vec4 settings = surfaceMetadata(10.0, index);
    float distanceToSurface = abs(dot(surfaceMetadata(8.0, index), world));
    if (settings.y > 1.5) {
      distanceToSurface = abs(length(ndc.xy) - 1.0) * settings.z;
      ndc = vec3(fract(atan(ndc.y, ndc.x) / 6.28318530718 + 0.5) * 2.0 - 1.0, ndc.z, 0.0);
    }
    if (projected.w <= 0.0 || any(greaterThan(abs(ndc), vec3(1.0)))) continue;
    vec2 pixel = clamp(floor((ndc.xy * 0.5 + 0.5) * bounds.xy), vec2(0.0), bounds.xy - 1.0);
    vec2 sampleValue = texture2D(uSurfaceAtlas, (bounds.zw + pixel + 0.5) / uSurfaceAtlasSize).rg;
    if (sampleValue.r < 0.5 || distanceToSurface > settings.x) continue;
    if (settings.y > 0.5) return true;
    vec4 captured = surfaceMatrix(4.0, index) * vec4(ndc.xy, sampleValue.g * 2.0 - 1.0, 1.0);
    if (distance(captured.xyz / captured.w, world.xyz) <= settings.x * 3.0) return true;
  }
#endif
  return false;
}
`;

type FilterHook = {
  source(): string;
  active(): boolean;
  key(): string;
  uniforms: ShaderMaterial["uniforms"];
  enabled(camera: Camera): void;
};
const attachments = new WeakMap<
  Material,
  {
    compile: Material["onBeforeCompile"];
    render: Material["onBeforeRender"];
    key: Material["customProgramCacheKey"];
    filters: Map<string, FilterHook>;
  }
>();

/** Union-of-selections point opacity without one sampler per plane. */
export class SurfacePointFilter extends FullScreenPass<ShaderMaterial> {
  private readonly vertexPredicate: string;
  private atlas: DataTexture | undefined;
  private metadata: DataTexture | undefined;
  private cells: DataTexture | undefined;
  private readonly namespace = `filter${MathUtils.generateUUID().replaceAll("-", "")}`;
  private readonly state = { enabled: true };
  private readonly materials = new Set<Material>();
  private variant = "off";
  private readonly uniforms = {
    uColor: new Uniform<Texture | null>(null),
    uDepth: new Uniform<Texture | null>(null),
    uInverse: { value: new Matrix4() },
    uSurfaceAtlas: { value: new DataTexture() },
    uSurfaceMetadata: { value: new DataTexture() },
    uSurfaceAtlasSize: { value: [1, 1] },
    uSurfaceCount: { value: 0 },
    uOtherPointsOpacity: { value: 1 },
    uSurfaceFilterEnabled: { value: false },
    uUseCells: { value: false },
    uCellHash: { value: new DataTexture() },
    uCellHashSize: { value: 1 },
    uCellHashWidth: { value: 1 },
    uCellFrame: { value: new Matrix4() },
  };

  /**
   * @param camera Only filter this camera's point-cloud view.
   * @param predicate Optional shared world-space selection predicate for post-shading and pre-depth filtering.
   * @param includeAnalytic Union a custom predicate with the existing plane/cylinder selections.
   */
  constructor(
    readonly camera: Camera,
    predicate?: {
      shader: string;
      vertexShader: string;
      uniforms: ShaderMaterial["uniforms"];
    },
    includeAnalytic = false,
  ) {
    function combined(source: string): string {
      return `${shader.replaceAll("selectedSurfacePoint", "analyticSurfacePoint")}\n${source.replaceAll("selectedSurfacePoint", "pipeSurfacePoint")}\nbool selectedSurfacePoint(vec4 world) { return analyticSurfacePoint(world) || pipeSurfacePoint(world); }`;
    }
    function selectionCode(source?: string): string {
      if (!source) return shader;
      if (includeAnalytic) return combined(source);
      return `uniform float uOtherPointsOpacity; uniform bool uSurfaceFilterEnabled;\n${source}`;
    }
    const selectionShader = selectionCode(predicate?.shader);
    super(
      new ShaderMaterial({
        blending: NoBlending,
        vertexShader: `varying vec2 vUv;
        void main() { vUv = uv; gl_Position = vec4(position.xy, 0.0, 1.0); }`,
        fragmentShader: `precision highp float;
        uniform sampler2D uColor;
        uniform sampler2D uDepth;
        uniform mat4 uInverse;
        varying vec2 vUv;
        ${selectionShader}
        void main() {
          float depth = texture2D(uDepth, vUv).r;
          vec4 color = texture2D(uColor, vUv);
          gl_FragDepth = depth;
          if (uSurfaceFilterEnabled && depth < 1.0 && color.a > 0.0) {
            vec4 world = uInverse * vec4(vUv * 2.0 - 1.0, depth * 2.0 - 1.0, 1.0);
            world /= world.w;
            if (!selectedSurfacePoint(world)) color.a *= uOtherPointsOpacity;
          }
          gl_FragColor = color;
          #include <tonemapping_fragment>
          #include <colorspace_fragment>
        }`,
      }),
    );
    this.vertexPredicate = selectionCode(predicate?.vertexShader);
    Object.assign(this.uniforms, predicate?.uniforms);
    this.material.uniforms = this.uniforms;
    this.variant = this.shaderVariant();
  }

  private preDepthActive(): boolean {
    return this.state.enabled && this.uniforms.uOtherPointsOpacity.value === 0;
  }

  private shaderVariant(): string {
    if (!this.preDepthActive()) return "off";
    const pipeCount = Object.hasOwn(this.material.uniforms, "uPipeNodeCount")
      ? this.material.uniforms.uPipeNodeCount.value
      : undefined;
    return `${this.uniforms.uUseCells.value}:${this.uniforms.uSurfaceCount.value > 0}:${typeof pipeCount === "number" && pipeCount > 0}`;
  }

  private refreshVariant(): void {
    const next = this.shaderVariant();
    if (next === this.variant) return;
    this.variant = next;
    for (const material of this.materials) material.needsUpdate = true;
  }

  private specializedPredicate(): string {
    const defines: string[] = [];
    if (!this.uniforms.uUseCells.value) {
      defines.push("#define SURFACE_FILTER_SKIP_CELLS");
    }
    if (this.uniforms.uUseCells.value || !this.uniforms.uSurfaceCount.value) {
      defines.push("#define SURFACE_FILTER_SKIP_ANALYTIC");
    }
    const pipeCount = Object.hasOwn(this.material.uniforms, "uPipeNodeCount")
      ? this.material.uniforms.uPipeNodeCount.value
      : undefined;
    if (pipeCount === 0) defines.push("#define SURFACE_FILTER_SKIP_PIPE");
    return `${defines.join("\n")}\n${this.vertexPredicate}`;
  }

  /** @param enabled Whether selection filtering is active. */
  setEnabled(enabled: boolean): void {
    this.state.enabled = enabled;
    this.enabled = enabled;
    this.refreshVariant();
  }

  /** @param opacity Opacity for points outside the union. */
  setOpacity(opacity: number): void {
    this.uniforms.uOtherPointsOpacity.value = opacity;
    this.refreshVariant();
  }

  /**
   * @param cells Completed quarter-metre cells in the recorded coverage frame.
   * @param frame Coverage frame to current world coordinates.
   * @param origin Grid origin in the coverage frame.
   */
  setCells(
    cells: Array<[number[], number[]]>,
    frame = new Matrix4(),
    origin = new Vector3(),
  ): void {
    this.cells?.dispose();
    const indices = [
      ...new Map(
        cells.map(([minimum]) => {
          const cell = new Vector3()
            .fromArray(minimum)
            .sub(origin)
            .multiplyScalar(4)
            .round()
            .toArray();
          return [cell.join(","), cell];
        }),
      ).values(),
    ];
    let size = MathUtils.ceilPowerOfTwo(Math.max(4, indices.length * 4));
    let pixels: Float32Array;
    for (;;) {
      if (size > MAX_CELL_HASH_SIZE) {
        throw new Error("Processed-cell mask exceeds the display memory limit");
      }
      pixels = new Float32Array(size * 4);
      let complete = true;
      for (const coordinates of indices) {
        let slot =
          ((Math.imul(coordinates[0], CELL_HASH_X) ^
            Math.imul(coordinates[1], CELL_HASH_Y) ^
            Math.imul(coordinates[2], CELL_HASH_Z)) >>>
            0) %
          size;
        let probe = 0;
        while (pixels[slot * 4 + 3] && probe++ < 64) slot = (slot + 1) % size;
        if (probe >= 64) {
          complete = false;
          break;
        }
        pixels.set([...coordinates, 1], slot * 4);
      }
      if (complete) break;
      size *= 2;
    }
    const width = MathUtils.ceilPowerOfTwo(Math.ceil(Math.sqrt(size)));
    const padded = new Float32Array(width * width * 4);
    padded.set(pixels);
    this.cells = new DataTexture(padded, width, width, RGBAFormat, FloatType);
    this.cells.minFilter = NearestFilter;
    this.cells.magFilter = NearestFilter;
    this.cells.needsUpdate = true;
    this.uniforms.uCellHash.value = this.cells;
    this.uniforms.uCellHashSize.value = size;
    this.uniforms.uCellHashWidth.value = width;
    this.uniforms.uCellFrame.value
      .makeTranslation(-origin.x, -origin.y, -origin.z)
      .multiply(frame.clone().invert());
    this.uniforms.uUseCells.value = true;
    this.refreshVariant();
  }

  /** @param selections All selected patches belonging to this point cloud. */
  setSelections(selections: SurfaceSelection[]): void {
    this.atlas?.dispose();
    this.metadata?.dispose();
    const cells = selections.reduce(
      (sum, selection) => sum + selection.width * selection.height,
      0,
    );
    const width = MathUtils.ceilPowerOfTwo(
      Math.max(
        1,
        Math.ceil(Math.sqrt(cells)),
        ...selections.map((selection) => selection.width),
      ),
    );
    let column = 0;
    let row = 0;
    let rowHeight = 0;
    const placements = selections.map((selection) => {
      if (column + selection.width > width) {
        row += rowHeight;
        column = 0;
        rowHeight = 0;
      }
      const placement = { column, row };
      column += selection.width;
      rowHeight = Math.max(rowHeight, selection.height);
      return placement;
    });
    const height = Math.max(1, row + rowHeight);
    const pixels = new Float32Array(width * height * 2);
    const metadata = new Float32Array(
      METADATA_COLUMNS * 4 * Math.max(1, selections.length),
    );
    selections.forEach((selection, index) => {
      const placement = placements[index];
      for (let row = 0; row < selection.height; row++) {
        for (let column = 0; column < selection.width; column++) {
          const source = row * selection.width + column;
          const target =
            ((placement.row + row) * width + placement.column + column) * 2;
          pixels[target] = selection.mask[source] ? 1 : 0;
          pixels[target + 1] = selection.worldSpace
            ? 0.5
            : selection.depth[source];
        }
      }
      const offset = index * METADATA_COLUMNS * 4;
      metadata.set(selection.viewProjection.elements, offset);
      metadata.set(
        selection.inverseViewProjection.elements,
        offset + MATRIX_COLUMNS * 4,
      );
      metadata.set(selection.plane.toArray(), offset + 8 * 4);
      metadata.set(
        [selection.width, selection.height, placement.column, placement.row],
        offset + 9 * 4,
      );
      metadata.set(
        [
          selection.tolerance,
          selection.cylinder ? 2 : Number(!!selection.worldSpace),
          selection.cylinder?.radius ?? 0,
          0,
        ],
        offset + 10 * 4,
      );
    });
    this.atlas = new DataTexture(pixels, width, height, RGFormat, FloatType);
    this.metadata = new DataTexture(
      metadata,
      METADATA_COLUMNS,
      Math.max(1, selections.length),
      RGBAFormat,
      FloatType,
    );
    for (const texture of [this.atlas, this.metadata]) {
      texture.minFilter = NearestFilter;
      texture.magFilter = NearestFilter;
      texture.needsUpdate = true;
    }
    this.uniforms.uSurfaceAtlas.value = this.atlas;
    this.uniforms.uSurfaceMetadata.value = this.metadata;
    this.uniforms.uSurfaceAtlasSize.value = [width, height];
    this.uniforms.uSurfaceCount.value = selections.length;
    this.uniforms.uUseCells.value = false;
    this.refreshVariant();
  }

  /**
   * @param material Shared point material.
   * @returns Restore the original material configuration.
   */
  attach(material: Material): () => void {
    const { uniforms, state, camera: filterCamera } = this;
    let entry = attachments.get(material);
    if (!entry) {
      entry = {
        compile: material.onBeforeCompile,
        render: material.onBeforeRender,
        key: material.customProgramCacheKey,
        filters: new Map(),
      };
      attachments.set(material, entry);
      const active = entry;
      material.onBeforeCompile = function compileFilters(
        program,
        renderer,
      ): void {
        active.compile.call(this, program, renderer);
        for (const [name, filter] of active.filters) {
          Object.assign(program.uniforms, filter.uniforms);
          if (!filter.active()) continue;
          program.vertexShader =
            `${filter.source()}\n${program.vertexShader}`.replace(
              "void main() {",
              `void main() {
            if (${name}_uSurfaceFilterEnabled && ${name}_uOtherPointsOpacity == 0.0 && !${name}_selectedSurfacePoint(modelMatrix * vec4(position, 1.0))) {
              gl_Position = vec4(2.0, 2.0, 2.0, 1.0); gl_PointSize = 0.0; return;
            }`,
            );
        }
      };
      material.onBeforeRender = function renderFilters(
        renderer,
        scene,
        camera,
        geometry,
        object,
        group,
      ): void {
        active.render.call(
          this,
          renderer,
          scene,
          camera,
          geometry,
          object,
          group,
        );
        for (const filter of active.filters.values()) filter.enabled(camera);
        if ("uniformsNeedUpdate" in this) this.uniformsNeedUpdate = true;
      };
      material.customProgramCacheKey = function filterKey(): string {
        return `${active.key.call(this)}:surface-filters:${[...active.filters]
          .filter(([, filter]) => filter.active())
          .map(([name, filter]) => `${name}:${filter.key()}`)
          .join(":")}`;
      };
    }
    const name = this.namespace;
    entry.filters.set(name, {
      source: () =>
        this.specializedPredicate().replace(FILTER_IDENTIFIERS, `${name}_$1`),
      active: () => this.preDepthActive(),
      key: () => this.shaderVariant(),
      uniforms: Object.fromEntries(
        Object.entries(uniforms).map(([key, value]) => [
          `${name}_${key}`,
          value,
        ]),
      ),
      enabled: (camera) => {
        uniforms.uSurfaceFilterEnabled.value =
          state.enabled && camera === filterCamera;
      },
    });
    this.materials.add(material);
    material.needsUpdate = true;
    return () => {
      this.materials.delete(material);
      entry.filters.delete(name);
      if (!entry.filters.size) {
        material.onBeforeCompile = entry.compile;
        material.onBeforeRender = entry.render;
        material.customProgramCacheKey = entry.key;
        attachments.delete(material);
      }
      material.needsUpdate = true;
    };
  }

  /**
   * @param renderer Active renderer.
   * @param writeBuffer Destination target.
   * @param readBuffer Fully shaded point-cloud color and depth.
   */
  render(
    renderer: WebGLRenderer,
    writeBuffer: WebGLRenderTarget,
    readBuffer: WebGLRenderTarget,
  ): void {
    this.uniforms.uColor.value = readBuffer.texture;
    this.uniforms.uDepth.value = readBuffer.depthTexture;
    this.uniforms.uInverse.value.multiplyMatrices(
      this.camera.matrixWorld,
      this.camera.projectionMatrixInverse,
    );
    this.uniforms.uSurfaceFilterEnabled.value = this.state.enabled;
    renderer.setRenderTarget(this.renderToScreen ? null : writeBuffer);
    renderer.clear();
    this.fsQuad.render(renderer);
  }

  /** Release packed selection textures. */
  override dispose(): void {
    this.cells?.dispose();
    this.atlas?.dispose();
    this.metadata?.dispose();
    super.dispose();
  }
}
